import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Loader from "../../Components/Loader";
import "../../Styles/AdminDetails.css"; // Reusing existing styles

function AdminStudents() {
  const { adminId } = useParams();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState("");
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        // First, get admin details to display admin name
        const adminResponse = await axios.get(
          `https://api.uniums.com/api/dashboard/admin/${adminId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (adminResponse.data && adminResponse.data.data) {
          setAdminName(adminResponse.data.data.name);
        }

        // Then, get students for this admin
        const response = await axios.get(
          `https://api.uniums.com/api/dashboard/${adminId}/students`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data && response.data.data) {
          setStudents(response.data.data);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching student data:", error);
        setError("Failed to load student data. Please try again later.");
        setLoading(false);
      }
    };

    fetchStudents();
  }, [adminId]);

  const handleDeleteClick = (student) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setStudentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!studentToDelete) return;
    
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setDeleteError("No authentication token found");
        setDeleteLoading(false);
        return;
      }

      // Call API to delete the student
      await axios.delete(
        `https://api.uniums.com/api/dashboard/student/${studentToDelete._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Remove deleted student from the state
      setStudents(students.filter(s => s._id !== studentToDelete._id));
      setShowDeleteModal(false);
      setStudentToDelete(null);
    } catch (error) {
      console.error("Error deleting student:", error);
      setDeleteError("Failed to delete student. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Loader />
        <div className="admin-details-loading">Loading student details...</div>
      </>
    );
  }

  if (error) {
    return <div className="admin-details-error">{error}</div>;
  }

  return (
    <div className="admin-details-main-container">
      <h2 className="admin-details-header-title">
        Students under {adminName || "Admin"}
      </h2>
      {students.length === 0 ? (
        <div className="admin-details-no-data">
          No students found for this admin.
        </div>
      ) : (
        <div className="admin-details-table-wrapper">
          <table className="admin-details-data-table">
            <thead>
              <tr>
                <th className="admin-details-table-header">Name</th>
                <th className="admin-details-table-header">Email</th>
                <th className="admin-details-table-header">Employee Code</th>
                <th className="admin-details-table-header">Gender</th>
                <th className="admin-details-table-header">Status</th>
                <th className="admin-details-table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <tr key={student._id} className="admin-details-table-row">
                  <td className="admin-details-table-cell">{student.name}</td>
                  <td className="admin-details-table-cell">{student.email}</td>
                  <td className="admin-details-table-cell">
                    {student.employeeCode || "N/A"}
                  </td>
                  <td className="admin-details-table-cell">
                    {student.gender || "N/A"}
                  </td>
                  <td className="admin-details-table-cell">
                    <span
                      className={`admin-details-status-badge ${
                        student.active
                          ? "admin-details-status-active"
                          : "admin-details-status-inactive"
                      }`}
                    >
                      {student.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="admin-details-action-cell">
                    <button
                      className="admin-details-delete-button"
                      onClick={() => handleDeleteClick(student)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-container">
            <div className="admin-modal-header">
              <h3>Confirm Deletion</h3>
            </div>
            <div className="admin-modal-body">
              <p>
                Are you sure you want to delete the student "{studentToDelete?.name}"?
                This action cannot be undone.
              </p>
              {deleteError && (
                <div className="admin-modal-error">{deleteError}</div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-modal-cancel-button"
                onClick={handleCancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="admin-modal-confirm-button"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminStudents;